/**
 * Illustration registry — empty-state scenes, one motif per concept.
 *
 * The SECOND sanctioned home for `<svg>` markup after `ui/icons.js` (the UI
 * consistency sweep skips exactly these two files for `no-inline-svg-literal`;
 * `no-sized-svg-attr` still applies here and must). Icons are 24×24 glyphs
 * that inherit `currentColor`; these are 140×110 *scenes* painted exclusively
 * with the `--pb-illo-*` tokens (defined on `.pb-illo` in tokens.css), so a
 * scene re-themes inside a `.pb-dark` subtree and under forced-colors without
 * a single hardcoded color. Both invariants are swept, not documented.
 *
 * ── The rules, baked into the emitter rather than documented ──
 *   · one viewBox, `0 0 140 110`; `fill="none"` root; `aria-hidden="true"`
 *   · no `width`/`height` attribute, ever — the `.pb-illo` container sizes it
 *   · every paint is `none` or `var(--pb-illo-*)` — never hex, never
 *     `currentColor` (a scene inheriting button ink would be wrong everywhere)
 *   · one motif per concept; the STATE lives in a swappable top-right badge
 *     (plus = empty, funnel = filtered, magnifier = search, alert = error),
 *     so four states never cost four drawings
 *   · no animation — scenes are static, so reduced-motion needs no register
 *   · an unknown name returns `''` and reports — a missing scene must never
 *     throw inside a template literal and take a whole panel down with it
 *
 * The output is TRUSTED STATIC MARKUP. It is meant for raw `innerHTML` on a
 * `.pb-illo` holder and must NEVER be routed through `escapeHTML` — escaping
 * it renders literal `<svg` text.
 *
 * ── Where a scene belongs ──
 * Modal and panel empty states the user dwells in (project lists, the fleet
 * view, the comment pane). NOT popovers, comboboxes, or transient result
 * lists (command palette, find & replace, slash menu, font search): those are
 * keyboard-driven and height-sensitive, and a scene shifts their anchor
 * geometry mid-interaction. Text — plus an existing 24px `icon()` where one
 * is already present — is the correct treatment there. Do not relitigate this
 * per surface.
 *
 * Usage:
 *   illustration('fleet')                       → HTML string, plus badge
 *   illustration('fleet', { variant: 'error' }) → same motif, alert badge
 *   illustrationEl('fleet')                     → a real <svg> node
 */

import { reportError } from '../core/report-error.js';

/** Paint shorthands — every paint in this file goes through one of these. */
const INK = 'var(--pb-illo-ink)';
const INK_STRONG = 'var(--pb-illo-ink-strong)';
const PAPER = 'var(--pb-illo-paper)';
const SHADOW = 'var(--pb-illo-shadow)';
const ACCENT = 'var(--pb-illo-accent)';

/** Shared ground the motif stands on — same ellipse in every scene. */
const GROUND_SHADOW = `<ellipse cx="70" cy="103" rx="42" ry="3.5" fill="${SHADOW}"/>`;

/**
 * State badges, all centered on (110, 20) so a variant swap moves nothing.
 * Paper-filled disc over the motif, accent ring, accent glyph.
 */
const _badge = (glyph) =>
  `<circle cx="110" cy="20" r="12.5" fill="${PAPER}" stroke="${ACCENT}" stroke-width="2"/>` +
  `<circle cx="110" cy="20" r="12.5" fill="${ACCENT}" fill-opacity="0.16"/>` +
  glyph;

const BADGES = Object.freeze({
  /* plus — nothing here yet; the next step is to add */
  empty: _badge(
    `<path d="M110 14.5v11M104.5 20h11" stroke="${ACCENT}" stroke-width="2" stroke-linecap="round"/>`
  ),
  /* funnel — a filter is hiding everything */
  filtered: _badge(
    `<path d="M103.5 15h13l-5 6v5.5l-3 2V21z" fill="${ACCENT}" fill-opacity="0.3" ` +
      `stroke="${ACCENT}" stroke-width="1.6" stroke-linejoin="round"/>`
  ),
  /* magnifier — a search matched nothing */
  search: _badge(
    `<circle cx="108" cy="18" r="5" stroke="${ACCENT}" stroke-width="2"/>` +
      `<path d="M111.8 21.8l4.2 4.2" stroke="${ACCENT}" stroke-width="2.4" stroke-linecap="round"/>`
  ),
  /* alert — the load failed; the list is not known to be empty */
  error: _badge(
    `<path d="M110 13.5v7.5" stroke="${ACCENT}" stroke-width="2.4" stroke-linecap="round"/>` +
      `<circle cx="110" cy="25.5" r="1.6" fill="${ACCENT}"/>`
  )
});

/**
 * The one canonical root. Every scene is motif + optional badge inside this
 * wrapper — the sweep asserts this file has exactly one `<svg` writer, which
 * is what makes the conventions above checkable against one function.
 */
const _scene = (motif, badge) =>
  '<svg viewBox="0 0 140 110" fill="none" aria-hidden="true">' +
  GROUND_SHADOW +
  motif +
  (badge || '') +
  '</svg>';

/** Small accent dots that give a scene a little life. */
const _spark = (cx, cy, r, o) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${ACCENT}" fill-opacity="${o}"/>`;

/**
 * One motif per concept. Drawn on the 140×110 grid, standing on the ground
 * ellipse (y≈103), keeping the top-right corner light so the badge reads.
 */
const MOTIFS = Object.freeze({
  /* Folder stack, front folder with a page corner peeking out — My Projects */
  projects:
    `<rect x="44" y="26" width="42" height="28" rx="3" fill="${PAPER}" stroke="${INK}" stroke-width="1.5"/>` +
    `<rect x="51" y="33" width="24" height="2.6" rx="1.3" fill="${INK}" fill-opacity="0.55"/>` +
    `<rect x="51" y="39" width="17" height="2.6" rx="1.3" fill="${INK}" fill-opacity="0.4"/>` +
    `<path d="M31 44h22l6 8h45a5 5 0 0 1 5 5v39a5 5 0 0 1-5 5H36a5 5 0 0 1-5-5V44z" ` +
    `fill="${PAPER}" stroke="${INK_STRONG}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M31 60h78" stroke="${INK}" stroke-width="1.5"/>` +
    _spark(26, 32, 2.2, 0.45) +
    _spark(116, 62, 2, 0.35),

  /* Three offset cards with a clock on the front card — versions, history */
  versions:
    `<rect x="48" y="22" width="54" height="36" rx="5" fill="${PAPER}" stroke="${INK}" stroke-width="1.5"/>` +
    `<rect x="42" y="32" width="54" height="36" rx="5" fill="${PAPER}" stroke="${INK}" stroke-width="1.5"/>` +
    `<rect x="36" y="42" width="58" height="42" rx="6" fill="${PAPER}" stroke="${INK_STRONG}" stroke-width="2"/>` +
    `<rect x="44" y="52" width="28" height="3" rx="1.5" fill="${INK}" fill-opacity="0.55"/>` +
    `<rect x="44" y="60" width="36" height="2.6" rx="1.3" fill="${INK}" fill-opacity="0.4"/>` +
    `<rect x="44" y="67" width="22" height="2.6" rx="1.3" fill="${INK}" fill-opacity="0.4"/>` +
    `<circle cx="96" cy="78" r="13" fill="${PAPER}" stroke="${ACCENT}" stroke-width="2"/>` +
    `<circle cx="96" cy="78" r="13" fill="${ACCENT}" fill-opacity="0.12"/>` +
    `<path d="M96 71v7l5 3" stroke="${ACCENT}" stroke-width="2" stroke-linecap="round" ` +
    `stroke-linejoin="round"/>` +
    _spark(28, 36, 2.2, 0.45),

  /* Browser window with an eye in the viewport — saved previews */
  previews:
    `<rect x="32" y="26" width="76" height="58" rx="7" fill="${PAPER}" stroke="${INK_STRONG}" stroke-width="2"/>` +
    `<path d="M32 41h76" stroke="${INK}" stroke-width="1.5"/>` +
    `<circle cx="40" cy="33.5" r="1.8" fill="${INK}"/>` +
    `<circle cx="46.5" cy="33.5" r="1.8" fill="${INK}"/>` +
    `<circle cx="53" cy="33.5" r="1.8" fill="${INK}"/>` +
    `<rect x="60" y="30.5" width="42" height="6" rx="3" fill="${INK}" fill-opacity="0.15"/>` +
    `<path d="M52 62c5-8 13-12 18-12s13 4 18 12c-5 8-13 12-18 12s-13-4-18-12z" ` +
    `stroke="${ACCENT}" stroke-width="2" stroke-linejoin="round"/>` +
    `<circle cx="70" cy="62" r="4.5" fill="${ACCENT}" fill-opacity="0.35" stroke="${ACCENT}" ` +
    `stroke-width="1.6"/>` +
    _spark(24, 52, 2.2, 0.45),

  /* Two overlapping sheets, front one dog-eared — page selection */
  pages:
    `<rect x="56" y="20" width="40" height="52" rx="4" fill="${PAPER}" stroke="${INK}" ` +
    `stroke-width="1.5" transform="rotate(4 76 46)"/>` +
    `<path d="M46 30h26l14 14v40a4 4 0 0 1-4 4H46a4 4 0 0 1-4-4V34a4 4 0 0 1 4-4z" ` +
    `fill="${PAPER}" stroke="${INK_STRONG}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M72 30v14h14" stroke="${INK_STRONG}" stroke-width="2" stroke-linejoin="round"/>` +
    `<rect x="50" y="52" width="26" height="3" rx="1.5" fill="${ACCENT}" fill-opacity="0.55"/>` +
    `<rect x="50" y="60" width="30" height="2.6" rx="1.3" fill="${INK}" fill-opacity="0.4"/>` +
    `<rect x="50" y="67" width="20" height="2.6" rx="1.3" fill="${INK}" fill-opacity="0.4"/>` +
    _spark(30, 40, 2.2, 0.45) +
    _spark(112, 66, 2, 0.35),

  /* Globe with two site cards on a dashed orbit — the client fleet */
  fleet:
    `<circle cx="70" cy="60" r="26" fill="${PAPER}" stroke="${INK_STRONG}" stroke-width="2"/>` +
    `<ellipse cx="70" cy="60" rx="11" ry="26" stroke="${INK}" stroke-width="1.5"/>` +
    `<path d="M46 52h48M46 68h48" stroke="${INK}" stroke-width="1.5"/>` +
    `<path d="M31 39a48 48 0 0 1 32-17M109 81a48 48 0 0 1-32 17" stroke="${ACCENT}" ` +
    `stroke-width="1.6" stroke-linecap="round" stroke-dasharray="4 5"/>` +
    `<rect x="20" y="42" width="24" height="17" rx="3" fill="${PAPER}" stroke="${ACCENT}" ` +
    `stroke-width="1.6"/>` +
    `<path d="M20 48h24" stroke="${ACCENT}" stroke-width="1.2"/>` +
    `<rect x="97" y="60" width="24" height="17" rx="3" fill="${PAPER}" stroke="${ACCENT}" ` +
    `stroke-width="1.6"/>` +
    `<path d="M97 66h24" stroke="${ACCENT}" stroke-width="1.2"/>` +
    _spark(38, 25, 2.2, 0.45),

  /* Three layout blocks slightly exploded — sections */
  sections:
    `<rect x="40" y="24" width="62" height="22" rx="4" fill="${ACCENT}" fill-opacity="0.16" ` +
    `stroke="${ACCENT}" stroke-width="1.8"/>` +
    `<rect x="47" y="31" width="26" height="3" rx="1.5" fill="${ACCENT}" fill-opacity="0.6"/>` +
    `<rect x="47" y="38" width="36" height="2.6" rx="1.3" fill="${ACCENT}" fill-opacity="0.4"/>` +
    `<rect x="34" y="52" width="30" height="20" rx="4" fill="${PAPER}" stroke="${INK_STRONG}" ` +
    `stroke-width="2"/>` +
    `<rect x="72" y="52" width="30" height="20" rx="4" fill="${PAPER}" stroke="${INK_STRONG}" ` +
    `stroke-width="2"/>` +
    `<rect x="40" y="78" width="62" height="14" rx="4" fill="${PAPER}" stroke="${INK}" ` +
    `stroke-width="1.5"/>` +
    _spark(26, 30, 2.2, 0.45) +
    _spark(112, 48, 2, 0.35),

  /* 2×2 grid of mini template cards, one lifted — template picker */
  templates:
    `<rect x="36" y="30" width="28" height="22" rx="3" fill="${PAPER}" stroke="${INK}" stroke-width="1.5"/>` +
    `<path d="M36 37h28" stroke="${INK}" stroke-width="1.2"/>` +
    `<rect x="70" y="30" width="28" height="22" rx="3" fill="${PAPER}" stroke="${INK}" stroke-width="1.5"/>` +
    `<path d="M70 37h28" stroke="${INK}" stroke-width="1.2"/>` +
    `<rect x="36" y="58" width="28" height="22" rx="3" fill="${PAPER}" stroke="${INK}" stroke-width="1.5"/>` +
    `<path d="M36 65h28" stroke="${INK}" stroke-width="1.2"/>` +
    `<rect x="68" y="55" width="30" height="24" rx="3" fill="${PAPER}" stroke="${ACCENT}" ` +
    `stroke-width="2" transform="rotate(5 83 67)"/>` +
    `<path d="M69.4 61.8l29.8 2.6" stroke="${ACCENT}" stroke-width="1.4"/>` +
    _spark(28, 24, 2.2, 0.45),

  /* Photo frame with mountains and a sun, second frame behind — images */
  images:
    `<rect x="52" y="24" width="52" height="40" rx="4" fill="${PAPER}" stroke="${INK}" ` +
    `stroke-width="1.5" transform="rotate(3 78 44)"/>` +
    `<rect x="36" y="36" width="56" height="44" rx="5" fill="${PAPER}" stroke="${INK_STRONG}" ` +
    `stroke-width="2"/>` +
    `<circle cx="76" cy="48" r="5" fill="${ACCENT}" fill-opacity="0.4" stroke="${ACCENT}" ` +
    `stroke-width="1.6"/>` +
    `<path d="M42 72l12-15 9 10 7-8 12 13" stroke="${INK_STRONG}" stroke-width="2" ` +
    `stroke-linecap="round" stroke-linejoin="round"/>` +
    _spark(26, 46, 2.2, 0.45) +
    _spark(110, 70, 2, 0.35),

  /* Two overlapping speech bubbles — comments */
  comments:
    `<path d="M62 24h42a6 6 0 0 1 6 6v18a6 6 0 0 1-6 6h-8l-8 9v-9h-26a6 6 0 0 1-6-6V30a6 6 0 0 1 6-6z" ` +
    `fill="${PAPER}" stroke="${INK}" stroke-width="1.5"/>` +
    `<circle cx="74" cy="39" r="2" fill="${INK}" fill-opacity="0.5"/>` +
    `<circle cx="83" cy="39" r="2" fill="${INK}" fill-opacity="0.5"/>` +
    `<circle cx="92" cy="39" r="2" fill="${INK}" fill-opacity="0.5"/>` +
    `<path d="M32 46h40a6 6 0 0 1 6 6v20a6 6 0 0 1-6 6H53l-9 10v-10h-12a6 6 0 0 1-6-6V52a6 6 0 0 1 6-6z" ` +
    `fill="${PAPER}" stroke="${INK_STRONG}" stroke-width="2"/>` +
    `<rect x="38" y="56" width="26" height="3" rx="1.5" fill="${ACCENT}" fill-opacity="0.55"/>` +
    `<rect x="38" y="64" width="19" height="2.6" rx="1.3" fill="${INK}" fill-opacity="0.4"/>` +
    _spark(114, 58, 2.2, 0.45),

  /* Paper plane on a dashed flight path toward a link chain — review links */
  links:
    `<path d="M34 46l64-18-26 46-10-17z" fill="${PAPER}" stroke="${INK_STRONG}" stroke-width="2" ` +
    `stroke-linejoin="round"/>` +
    `<path d="M98 28L62 57" stroke="${INK}" stroke-width="1.5" stroke-linejoin="round"/>` +
    `<path d="M40 86c16 8 36 6 50-6" stroke="${ACCENT}" stroke-width="1.6" ` +
    `stroke-linecap="round" stroke-dasharray="4 5"/>` +
    `<path d="M100 72a6 6 0 0 1 8.5 0l2 2a6 6 0 0 1-8.5 8.5" stroke="${ACCENT}" ` +
    `stroke-width="2" stroke-linecap="round"/>` +
    `<path d="M104 84a6 6 0 0 1-8.5 0l-2-2a6 6 0 0 1 8.5-8.5" stroke="${ACCENT}" ` +
    `stroke-width="2" stroke-linecap="round"/>` +
    _spark(28, 28, 2.2, 0.45),

  /* Two avatars on a shared card — the project team */
  team:
    `<rect x="36" y="40" width="68" height="46" rx="8" fill="${PAPER}" stroke="${INK_STRONG}" ` +
    `stroke-width="2"/>` +
    `<circle cx="57" cy="58" r="10" fill="${PAPER}" stroke="${INK}" stroke-width="1.8"/>` +
    `<circle cx="57" cy="55" r="3.4" fill="${INK}" fill-opacity="0.6"/>` +
    `<path d="M50.5 63.5a6.5 6.5 0 0 1 13 0" fill="${INK}" fill-opacity="0.6"/>` +
    `<circle cx="83" cy="58" r="10" fill="${PAPER}" stroke="${ACCENT}" stroke-width="1.8"/>` +
    `<circle cx="83" cy="55" r="3.4" fill="${ACCENT}" fill-opacity="0.6"/>` +
    `<path d="M76.5 63.5a6.5 6.5 0 0 1 13 0" fill="${ACCENT}" fill-opacity="0.6"/>` +
    `<rect x="52" y="74" width="36" height="3" rx="1.5" fill="${INK}" fill-opacity="0.35"/>` +
    _spark(28, 34, 2.2, 0.45) +
    _spark(112, 74, 2, 0.35),

  /* A key over a shield — passkeys */
  passkey:
    `<path d="M70 22l27 9.5V52c0 16.5-11 28-27 34-16-6-27-17.5-27-34V31.5z" fill="${PAPER}" ` +
    `stroke="${INK_STRONG}" stroke-width="2" stroke-linejoin="round"/>` +
    `<circle cx="63" cy="50" r="7" fill="${ACCENT}" fill-opacity="0.16" stroke="${ACCENT}" ` +
    `stroke-width="2"/>` +
    `<path d="M68.5 55.5l14 14M77 64l4-4M82.5 69.5l4-4" stroke="${ACCENT}" stroke-width="2" ` +
    `stroke-linecap="round"/>` +
    _spark(28, 30, 2.2, 0.45) +
    _spark(112, 44, 2, 0.35)
});

export const ILLUSTRATION_NAMES = Object.freeze(Object.keys(MOTIFS));
export const ILLUSTRATION_VARIANTS = Object.freeze(Object.keys(BADGES));

/**
 * Render a scene as an HTML string.
 *
 * @param {string} name — a key of ILLUSTRATION_NAMES
 * @param {object} [opts]
 * @param {'empty'|'filtered'|'search'|'error'} [opts.variant='empty'] — which
 *   state badge the motif wears; an unknown variant degrades to 'empty'
 * @returns {string} HTML, or '' for an unknown name
 */
export function illustration(name, opts = {}) {
  const motif = MOTIFS[name];
  if (!motif) {
    // Silent to the user by design (same contract as icons.js): a template
    // literal that interpolates a missing scene must degrade to text with no
    // artwork, not to a toast storm.
    reportError('illustrations', new Error(`unknown illustration "${name}"`), { user: 'silent' });
    return '';
  }
  const { variant = 'empty' } = opts;
  const badge = Object.hasOwn(BADGES, variant) ? BADGES[variant] : BADGES.empty;
  return _scene(motif, badge);
}

/**
 * The same scene as a live node. Parsed as `text/html`, NOT `image/svg+xml` —
 * see iconEl in ui/icons.js for why (namespace handling without `xmlns`).
 *
 * @returns {SVGElement|null} null for an unknown name
 */
export function illustrationEl(name, opts = {}) {
  const markup = illustration(name, opts);
  if (!markup) return null;
  if (typeof DOMParser !== 'function' || typeof document === 'undefined') return null;
  const el = new DOMParser().parseFromString(markup, 'text/html').body.querySelector('svg');
  if (!el) {
    reportError('illustrations', new Error(`illustration "${name}" failed to parse`), {
      user: 'silent'
    });
    return null;
  }
  return document.importNode(el, true);
}
